import { DataManager } from '@syncfusion/ej2-data';
import { LanguageService } from '../../services/language.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  ClientDTO,
  IParkerCompanyDTO,
  LinkedParkerDTO,
  Role,
  UserControlPlateDTO,
  UserInfoDTO,
} from '../../models/models.swagger.generated';
import { DropDownConstantsFactory } from '../../shared/syncfusion/dropdown-constants-factory';
import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { Observable, of, Subscription } from 'rxjs';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { ClientService } from '../../services/client/client.service';
import { startWith } from 'rxjs/operators';
import { LinkedParkerComponent } from '../linked-parker/linked-parker.component';
import { DepositedNumberPlatesComponent } from '../deposited-number-plates/deposited-number-plates.component';
import { YesNoGroupButtonComponent } from '../../shared/yes-no-group-button/yes-no-group-button.component';
import { ButtonGroupComponent } from '../../shared/button-group/button-group.component';
import { DropDownListComponent, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { ClientAutocompleteComponent } from '../../shared/client-autocomplete/client-autocomplete.component';
import { AppErrorMessagesComponent } from '../../error-messages/app-error-messages.component';
import { NgClass } from '@angular/common';
import { UserEditFormGroup } from './user-edit-form-type';
import { SimpleGetCustomAdaptor } from '../../shared/syncfusion/simple-get-custom-adaptor';
import { CompanyService } from '../../services/company.service';
import { AddressAutofillComponent } from "../../shared/address-autofill/address-autofill.component";
import { fullAddressWithAutocompleteValidator } from './user-edit-form-validators';

@Component({
    selector: 'app-user-edit-form',
    templateUrl: './user-edit-form.component.html',
    styleUrls: ['./user-edit-form.component.scss'],
    imports: [ReactiveFormsModule, NgClass, AppErrorMessagesComponent, ClientAutocompleteComponent, DropDownListModule, TooltipModule, ButtonGroupComponent, YesNoGroupButtonComponent, DepositedNumberPlatesComponent, LinkedParkerComponent, TranslateModule, AddressAutofillComponent],
    providers: [CompanyService]
})
export class UserEditFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() public userEditForm: UserEditFormGroup;
  @Input() public selectedClientId: number | null | undefined = null;
  @Input() public selectedUser: UserInfoDTO | null = null;

  @ViewChild('companydd') private companyDropDown: DropDownListComponent;

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public languageDropdownSettings = { text: 'value', value: 'key' };
  public isAdd = true;
  public languageDropDowItems: any[] = [];
  public hiddenRoles: Role[] = [Role.None, Role.User, Role.Proxy, Role.CompanyAdmin, Role.Thing, Role.Status];
  public Role = Role;
  public initialRole: Role;
  public companiesData: DataManager;

  public get formFields() {
    return this.userEditForm.controls;
  }
  public get clients(): ClientDTO[] {
    return this.clientService.clients;
  }
  public get isParkingRanger(): boolean {
    return this.userEditForm.value.role === Role.ParkingRanger;
  }

  public get isLoggedInUser(): boolean {
    return this.userEditForm.value.userId === this.authService.getUserInfo().userId;
  }

  public get controlPlates(): UserControlPlateDTO[] {
    return this.userEditForm.controls.controlPlates.value;
  }

  public set controlPlates(data: UserControlPlateDTO[]) {
    this.userEditForm.controls.controlPlates.setValue(data);
  }

  public get linkedParkers(): LinkedParkerDTO[] {
    return this.userEditForm.controls.linkedParkers.value;
  }

  public set linkedParkers(data: LinkedParkerDTO[]) {
    this.userEditForm.controls.linkedParkers.setValue(data);
  }

  private subscriptions = new Subscription();

  constructor(
    public authService: AuthService,
    private clientService: ClientService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private companyService: CompanyService
  ) {
  }

  public ngOnInit() {
    this.isAdd = !this.userEditForm.value.userId;
    this.initialRole = this.userEditForm.value.role;

    this.initializeForms();
    this.loadClients();
    if (this.authService.isUserAdmin()) {
      this.hiddenRoles = [Role.None, Role.Proxy, Role.User, Role.ParkingRanger, Role.ThirdPartyParkingControl, Role.CompanyAdmin, Role.BIAdmin, Role.BIClientUser, Role.Thing, Role.Status];
      if (!this.isAdd && (this.initialRole == Role.ClientAdmin || this.initialRole == Role.ClientUser)) {
        this.hiddenRoles = [...this.hiddenRoles, Role.Admin];
      }
    } else {
      // case client admin
      this.hiddenRoles = [Role.None, Role.Admin, Role.Proxy, Role.User, Role.ParkingRanger, Role.ThirdPartyParkingControl, Role.CompanyAdmin, Role.BIAdmin, Role.BIClientUser, Role.Thing, Role.Status];
    }
    this.companiesData = new DataManager({
          adaptor: new SimpleGetCustomAdaptor<IParkerCompanyDTO>(this.getCompanies.bind(this)),
        });
    this.subscriptions.add(this.userEditForm.controls.role.valueChanges.subscribe((v: Role) => {
      if (v === Role.Admin || v === Role.User) {
        if (v === Role.Admin || v === Role.User) {
          this.userEditForm.controls.client.reset();
        }
        this.userEditForm.controls.client.setValidators([]);
        this.userEditForm.controls.client.disable({ emitEvent: false });
      } else {
        this.userEditForm.controls.client.setValidators([Validators.required]);
        this.userEditForm.controls.client.enable({ emitEvent: false });
      }
      this.userEditForm.controls.client.updateValueAndValidity({ emitEvent: false });
    }));

    this.subscriptions.add(this.userEditForm.controls.client.valueChanges.subscribe((client: ClientDTO) => {
      if (this.companyDropDown) {
        // force a reload of the company dropdown data by applying a new DataManager
        this.companyDropDown.dataSource = new DataManager({
            adaptor: new SimpleGetCustomAdaptor<IParkerCompanyDTO>(this.getCompanies.bind(this)),
          });
      }
    }));

    this.subscriptions.add(this.userEditForm.controls.companyId.valueChanges.subscribe((companyId: number) => {
      if (companyId) {
        // remove CompayAdmin role from hidden roles if company is selected
        this.hiddenRoles = this.hiddenRoles.filter(r => r !== Role.CompanyAdmin);
      } else {
        // add CompayAdmin role to hidden roles if company is not selected
        this.hiddenRoles.push(Role.CompanyAdmin);
        this.hiddenRoles = [...this.hiddenRoles];
      }
      this.userEditForm.controls.role.updateValueAndValidity({ emitEvent: false });
      this.userEditForm.controls.companyId.updateValueAndValidity({ emitEvent: false });
    }));
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public hideClient() {
    return this.authService.getUserInfo().clientId || this.isLoggedInUser;
  }

  private initializeForms(): void {
    if (this.initialRole === Role.User) {
      this.userEditForm.controls.fullAddress.setValidators([
        fullAddressWithAutocompleteValidator(this)
      ]);
      this.userEditForm.controls.fullAddress.updateValueAndValidity({ emitEvent: false });
    }
    this.subscriptions.add(this.translate.onLangChange.pipe(startWith({})).subscribe(v => {
      this.languageDropDowItems = this.languageService.getLanguages()
        .map(l => ({ key: l, value: this.translate.instant('i18n.base.shared.Language.' + l) }));
    }));
  }

  private getClientById(id: number): ClientDTO {
    return id ? this.clients?.find(c => c.id === id) : null;
  }

  private loadClients() {
    const loggedInUser = this.authService.getUserInfo();
    if (loggedInUser.client != null) {
      this.userEditForm.controls.client.setValue(loggedInUser.client);
      if (loggedInUser.companyId != null) {
        this.userEditForm.controls.companyId.setValue(loggedInUser.companyId);
      }
    }
    else {
      // patch client to the form if required
      if (this.userEditForm.value.client && this.userEditForm.value.client.id) {
        this.userEditForm.controls.client.setValue(this.getClientById(this.userEditForm.value.client.id));
      } else if (this.isAdd) {
        this.userEditForm.controls.client.setValue(this.getClientById(this.selectedClientId));
      }
    }
  }

  private getCompanies(): Observable<IParkerCompanyDTO[]> {
    const clientId = (this.authService.getUserInfo().client ?? this.userEditForm.controls.client.getRawValue())?.id;
    if (clientId != null) {
      return this.companyService.getCompanies(clientId);
    }
    return of([]);
  }
}
